/**
 * The PHONE face of one production-record row, as tiles (PROD-1511).
 *
 * Every value the row has, in labelled tiles grouped like the paper: the
 * Transport counts, the Service counts, then the readings. A tap on a tile
 * opens the docked keypad on that value (`DockedKeypad`, owned by the wizard);
 * there is no second screen of stacked fields.
 *
 * A tile says how far to trust its number:
 *   - solid: someone entered it, typed or read off a photo;
 *   - DASHED: still Ordo's assumption (the order, the delivery plan, the
 *     kitchen's reading, every meal ordered as Served), with `Full order` under
 *     a defaulted Served;
 *   - an amber ring: a photo the reader found hard to read;
 *   - red: a reading outside its holding band;
 *   - amber fill: Served above Received, a note (backstock), never a blocker.
 *
 * A row with no readings to take (room temperature) says so in one line, and a
 * row that is not on the record (a weekly milk SKU) says it has no service
 * counts, instead of drawing tiles nobody can fill.
 *
 * Presentation only: every value and handler is the flow's row view.
 */

import { cn } from '@ordo/ui/cn'

import { TEMP_CLASS_CHIP, TEMP_CLASS_LABEL } from '@/components/cafeteria/temperature-table'
import {
  IN_KIT_LABEL,
  RECORD_SPREADSHEET_CHECK_HINT,
  RECORD_SPREADSHEET_COLUMNS,
  type RecordCell,
  type RecordColumnKey,
  type RecordSpreadsheetRowView,
} from '@/components/production/spreadsheet/record-spreadsheet-table'

/** Each tile's label: the digital record's own column header. */
export const TILE_LABEL: Record<RecordColumnKey, string> = Object.fromEntries(
  RECORD_SPREADSHEET_COLUMNS.map((column) => [column.key, column.header]),
) as Record<RecordColumnKey, string>

const TRANSPORT: readonly RecordColumnKey[] = ['ordered', 'prepared', 'received']
const SERVICE: readonly RecordColumnKey[] = ['served', 'reimbursable', 'nonReimbursable', 'leftover']
const TEMPS: readonly RecordColumnKey[] = ['kitchenTemp', 'receivedTemp', 'servingTemp']

export const ROOM_TEMP_NOTE = 'Room temp. No readings needed.'
export const NO_SERVICE_NOTE = 'No service counts on this record.'

/** A tile's number as shown: `—` for nothing yet, one decimal for a reading. */
export function tileDisplay(cell: RecordCell): string {
  if (cell.kind === 'count') {
    return cell.value === '' ? '—' : String(cell.value)
  }
  return cell.value === null ? '—' : cell.value.toFixed(1)
}

/** The cell has nowhere to save at all (as opposed to a signed or busy step): its `title` says why. */
function notOnRecord(cell: RecordCell): boolean {
  return cell.disabled === true && cell.title !== undefined
}

export interface RecordTileCardProps {
  row: RecordSpreadsheetRowView
  /** The tile the keypad is on, on this row; drawn with the focus ring. */
  activeColumn: RecordColumnKey | null
  onEdit: (column: RecordColumnKey) => void
}

export function RecordTileCard({ row, activeColumn, onEdit }: RecordTileCardProps) {
  const serviceOffered = SERVICE.some((column) => !notOnRecord(row.cells[column]))
  const readingsOffered = TEMPS.some((column) => {
    const cell = row.cells[column]
    return cell.kind === 'temp' && cell.offered
  })
  const alert = Object.values(row.cells).some((cell) => cell.alert)
  return (
    <article
      data-record-tile-card={row.key}
      aria-label={row.legend ? `${row.label}, ${row.legend}` : row.label}
      className={cn(
        'flex flex-col gap-2.5 rounded-xl border bg-card p-3.5',
        alert && 'border-red-300 dark:border-red-900',
      )}>
      <div>
        <h4 className="text-[15px] font-medium leading-[21px]">{row.title ?? row.label}</h4>
        <div className="flex flex-wrap items-center gap-2 text-[13px] leading-[18px] text-muted-foreground">
          {row.temperatureClass ? (
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[11px] font-medium leading-4',
                TEMP_CLASS_CHIP[row.temperatureClass],
              )}>
              {TEMP_CLASS_LABEL[row.temperatureClass]}
            </span>
          ) : null}
          {row.badge ? (
            <span className="rounded-sm bg-foreground/[0.06] px-1.5 text-[11px] font-medium leading-4 text-foreground/80">
              {row.badge}
            </span>
          ) : null}
          {(row.meta ?? row.legend) ? <span>{row.meta ?? row.legend}</span> : null}
        </div>
      </div>

      <TileRow row={row} columns={TRANSPORT} activeColumn={activeColumn} onEdit={onEdit} />
      {serviceOffered ? (
        <TileRow row={row} columns={SERVICE} activeColumn={activeColumn} onEdit={onEdit} />
      ) : (
        <p className="text-[13px] leading-[18px] text-muted-foreground">{NO_SERVICE_NOTE}</p>
      )}
      {readingsOffered ? (
        <TileRow row={row} columns={TEMPS} activeColumn={activeColumn} onEdit={onEdit} />
      ) : (
        <p className="text-[13px] leading-[18px] text-muted-foreground">{ROOM_TEMP_NOTE}</p>
      )}

      {/* A sealed kit's recipes (PROD-1441): count-less "in kit" lines, no inputs. */}
      {row.subRows && row.subRows.length > 0 ? (
        <ul className="space-y-0.5 border-l-2 border-border pl-2.5">
          {row.subRows.map((sub) => (
            <li key={sub.key} data-kit-sub-row={sub.key} className="text-xs">
              <span className="font-medium text-foreground/80">{sub.name}</span>
              <span className="ml-1.5 italic text-muted-foreground">{IN_KIT_LABEL}</span>
              {sub.legend ? <span className="block text-[11px] text-muted-foreground">{sub.legend}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}

      {row.footer ? <div>{row.footer}</div> : null}
    </article>
  )
}

function TileRow({
  row,
  columns,
  activeColumn,
  onEdit,
}: {
  row: RecordSpreadsheetRowView
  columns: readonly RecordColumnKey[]
  activeColumn: RecordColumnKey | null
  onEdit: (column: RecordColumnKey) => void
}) {
  return (
    <div className="grid grid-cols-4 gap-2" data-tile-row={columns[0]}>
      {columns.map((column) => (
        <Tile
          key={column}
          column={column}
          cell={row.cells[column]}
          active={activeColumn === column}
          onEdit={() => onEdit(column)}
        />
      ))}
    </div>
  )
}

function Tile({
  column,
  cell,
  active,
  onEdit,
}: {
  column: RecordColumnKey
  cell: RecordCell
  active: boolean
  onEdit: () => void
}) {
  const display = tileDisplay(cell)
  const empty = display === '—'
  const offered = cell.kind === 'count' || cell.offered
  const disabled = cell.disabled === true || !offered
  const flagged = cell.flag === 'check'
  return (
    <button
      type="button"
      data-tile={column}
      data-prefilled={cell.prefilled ? 'true' : undefined}
      aria-label={`${cell.ariaLabel}, ${empty ? 'empty' : display}${cell.prefilled ? ', pre-filled' : ''}`}
      aria-pressed={active}
      title={flagged ? RECORD_SPREADSHEET_CHECK_HINT : cell.title}
      disabled={disabled}
      onClick={onEdit}
      className={cn(
        'flex h-[60px] min-w-0 flex-col items-start justify-center gap-px rounded-[10px] border bg-background px-[9px] text-left transition-colors',
        cell.prefilled && 'border-dashed border-muted-foreground/45 bg-muted/40',
        flagged && 'ring-2 ring-amber-500/70',
        cell.alert && 'border-2 border-destructive bg-destructive/5',
        cell.note && 'border-2 border-amber-400 bg-amber-50 dark:border-amber-700 dark:bg-amber-950/40',
        active && 'border-2 border-primary bg-background ring-[3px] ring-primary/15',
        disabled && 'opacity-60',
      )}>
      <span
        className={cn(
          'truncate text-[11px] font-medium leading-[14px] text-muted-foreground',
          cell.alert && 'text-red-700 dark:text-red-400',
          active && 'text-primary',
        )}>
        {TILE_LABEL[column]}
      </span>
      <span
        className={cn(
          'text-[17px] font-medium leading-[21px] tabular-nums',
          empty && 'text-muted-foreground',
          cell.alert && 'text-red-700 dark:text-red-400',
        )}>
        {display}
      </span>
      {cell.caption ? (
        <span className={cn('truncate text-[10.5px] leading-3 text-muted-foreground', cell.alert && 'text-red-700')}>
          {cell.caption}
        </span>
      ) : null}
    </button>
  )
}
